import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EngineService } from '@/services/engine';
import { toast } from '@/components/Snackbar';
import { I18nNamespace } from '@/lib/i18n-namespaces';

const MIN_SUGGESTION_LENGTH = 5;
const MAX_SUGGESTION_LENGTH = 200;

interface UsePromptSuggestionOptions {
    prompt: string;
    onPromptChange: (text: string) => void;
    inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}

export function usePromptSuggestion({
    prompt,
    onPromptChange,
    inputRef,
}: UsePromptSuggestionOptions) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const [isLoading, setIsLoading] = useState(false);
    const [suggestedText, setSuggestedText] = useState('');
    const requestIdRef = useRef(0);

    const declineSuggestion = useCallback(() => {
        requestIdRef.current += 1;
        setIsLoading(false);
        setSuggestedText('');
    }, []);

    const requestSuggestion = useCallback(async () => {
        const text = prompt.trim();
        if (!text || isLoading) return;

        const requestId = ++requestIdRef.current;
        setSuggestedText('');
        setIsLoading(true);
        try {
            const { enhancedText } = await EngineService.suggestQuery(text);
            if (requestId !== requestIdRef.current) return;
            if (!enhancedText) throw new Error('Empty suggestion');
            setSuggestedText(enhancedText);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error('Failed to enhance prompt:', error);
            toast.error(t('promptInput.suggestion.error'));
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [prompt, isLoading, t]);

    const acceptSuggestion = useCallback(() => {
        if (!suggestedText) return;
        const text = suggestedText;
        declineSuggestion();

        const element = inputRef.current;
        if (element) {
            element.focus();
            element.select();
            // eslint-disable-next-line deprecation/deprecation -- no replacement API for undo-stack-aware text insertion
            document.execCommand('insertText', false, text);
        }
        onPromptChange(text);
    }, [suggestedText, inputRef, onPromptChange, declineSuggestion]);

    const lastPromptRef = useRef(prompt);
    useEffect(() => {
        const promptChanged = lastPromptRef.current !== prompt;
        lastPromptRef.current = prompt;
        if (promptChanged && (isLoading || suggestedText)) {
            declineSuggestion();
        }
    }, [prompt, isLoading, suggestedText, declineSuggestion]);

    return {
        isLoadingSuggestion: isLoading,
        suggestedText,
        canRequestSuggestion:
            prompt.trim().length > MIN_SUGGESTION_LENGTH &&
            prompt.trim().length <= MAX_SUGGESTION_LENGTH &&
            !isLoading &&
            !suggestedText,
        requestSuggestion,
        acceptSuggestion,
        declineSuggestion,
    };
}

export type PromptSuggestion = ReturnType<typeof usePromptSuggestion>;
